import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Upload, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface Overlay {
  id: string;
  name: string;
  url: string;
  defaultX: number;
  defaultY: number;
  defaultScale: number;
  defaultOpacity: number;
}

export function OverlaysPage() {
  const qc = useQueryClient();
  const user = useAuth((s) => s.user)!;
  const canEdit = user.isSuperAdmin || user.role === 'TENANT_ADMIN' || user.role === 'MANAGER';
  const overlays = useQuery({
    queryKey: ['overlays'],
    queryFn: () => api.get<Overlay[]>('/overlays'),
  });

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [defaultX, setX] = useState(0);
  const [defaultY, setY] = useState(0);
  const [defaultScale, setScale] = useState(1);
  const [defaultOpacity, setOpacity] = useState(1);

  const uploadMut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Pick a file');
      const fd = new FormData();
      fd.append('name', name.trim());
      fd.append('defaultX', String(defaultX));
      fd.append('defaultY', String(defaultY));
      fd.append('defaultScale', String(defaultScale));
      fd.append('defaultOpacity', String(defaultOpacity));
      fd.append('file', file);
      return api.post('/overlays', fd);
    },
    onSuccess: () => {
      toast.success('Overlay uploaded');
      setOpen(false); setName(''); setFile(null); setX(0); setY(0); setScale(1); setOpacity(1);
      qc.invalidateQueries({ queryKey: ['overlays'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Upload failed'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/overlays/${id}`),
    onSuccess: () => {
      toast.success('Overlay deleted');
      qc.invalidateQueries({ queryKey: ['overlays'] });
    },
  });

  return (
    <div className="container py-8 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Overlays</h1>
          <p className="text-sm text-muted-foreground">Transparent PNG branding applied over the final image.</p>
        </div>
        {canEdit && (
          <Button onClick={() => setOpen(true)} className="gap-2">
            <Upload className="h-4 w-4" /> Upload
          </Button>
        )}
      </header>

      {overlays.isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-56" />)}
        </div>
      )}

      {overlays.data && overlays.data.length === 0 && (
        <Card>
          <CardContent className="py-14 text-center">
            <div className="text-lg font-medium">No overlays yet.</div>
            <div className="text-sm text-muted-foreground">Upload a transparent PNG (logo, banner, price tag).</div>
          </CardContent>
        </Card>
      )}

      {overlays.data && overlays.data.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {overlays.data.map((o) => (
            <Card key={o.id} className="overflow-hidden">
              <div className="relative aspect-[4/3] bg-[conic-gradient(at_10%_10%,#2b3647_0deg,#1a2230_180deg)]">
                <img src={o.url} alt={o.name} className="h-full w-full object-contain p-6" />
              </div>
              <CardContent className="flex items-center justify-between gap-2 py-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{o.name}</div>
                  <div className="text-xs text-muted-foreground">scale {o.defaultScale.toFixed(2)} · opacity {Math.round(o.defaultOpacity * 100)}%</div>
                </div>
                {canEdit && (
                  <Button size="icon" variant="ghost" onClick={() => confirm('Delete this overlay?') && deleteMut.mutate(o.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload overlay</DialogTitle>
            <DialogDescription>Transparent PNG. Default placement can be re-adjusted per-car later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ov-name">Name</Label>
              <Input id="ov-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Dealer Logo" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ov-file">Image (PNG)</Label>
              <Input id="ov-file" type="file" accept="image/png" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </div>
            <div className="grid gap-3 sm:grid-cols-4">
              <div className="space-y-1">
                <Label className="text-xs">X</Label>
                <Input type="number" value={defaultX} onChange={(e) => setX(Number(e.target.value))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Y</Label>
                <Input type="number" value={defaultY} onChange={(e) => setY(Number(e.target.value))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Scale</Label>
                <Input type="number" step="0.05" min="0.1" max="5" value={defaultScale} onChange={(e) => setScale(Number(e.target.value))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Opacity</Label>
                <Input type="number" step="0.05" min="0" max="1" value={defaultOpacity} onChange={(e) => setOpacity(Number(e.target.value))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="ghost">Cancel</Button></DialogClose>
            <Button onClick={() => uploadMut.mutate()} disabled={!file || !name.trim() || uploadMut.isPending}>
              {uploadMut.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
